import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Search, ChevronDown } from 'lucide-react';

interface SkillsFilterProps {
  availableSkills: string[];
  selectedSkills: Set<string>;
  onSkillsChange: (skills: Set<string>) => void;
  className?: string;
}

export default function SkillsFilter({ availableSkills, selectedSkills, onSkillsChange, className }: SkillsFilterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredSkills = useMemo(() => {
    if (!searchQuery) return availableSkills;
    const query = searchQuery.toLowerCase();
    return availableSkills.filter(skill => 
      skill.toLowerCase().includes(query)
    );
  }, [availableSkills, searchQuery]);

  const handleSkillToggle = (skill: string) => {
    const newSkills = new Set(selectedSkills);
    if (newSkills.has(skill)) {
      newSkills.delete(skill);
    } else {
      newSkills.add(skill);
    }
    onSkillsChange(newSkills);
  };

  const handleSelectAll = () => {
    onSkillsChange(new Set(filteredSkills));
  };

  const handleClearAll = () => {
    onSkillsChange(new Set());
    setSearchQuery('');
  };

  const selectedCount = selectedSkills.size;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={`flex ${className ? '' : 'w-full'} items-center justify-between gap-2 text-sm whitespace-nowrap ${className || ''}`}
        >
          <span className="text-slate-700 truncate">
            {selectedCount > 0 ? `${selectedCount} Selected` : 'All Skills'}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Filter by Skills</h4>
            {selectedCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="h-auto p-1 text-xs"
              >
                Clear all
              </Button>
            )}
          </div>
          
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>

          {filteredSkills.length > 0 && (
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{filteredSkills.length} skills found</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                className="h-auto p-0 text-xs hover:bg-transparent underline"
              >
                Select all visible
              </Button>
            </div>
          )}
        </div>

        <ScrollArea className="h-[300px]">
          <div className="px-4 pb-4 space-y-2">
            {filteredSkills.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No skills found
              </p>
            ) : (
              filteredSkills.map((skill) => (
                <div
                  key={skill}
                  className="flex items-center space-x-2 hover:bg-muted/50 p-2 rounded-md cursor-pointer"
                  onClick={() => handleSkillToggle(skill)}
                >
                  <Checkbox
                    checked={selectedSkills.has(skill)}
                    onCheckedChange={() => handleSkillToggle(skill)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label className="text-sm flex-1 cursor-pointer">
                    {skill}
                  </label>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {selectedCount > 0 && (
          <div className="border-t p-4">
            <div className="flex flex-wrap gap-1">
              {Array.from(selectedSkills).slice(0, 3).map((skill) => (
                <Badge key={skill} variant="secondary" className="text-xs">
                  {skill}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSkillToggle(skill);
                    }}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedCount > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{selectedCount - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}